import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import OcAdminLayout from "@/components/layout/OcAdminLayout";
import {
  ocAdminGetSessionToken,
  ocAdminClearSessionToken,
  ocAdminRecordActivity,
  ocAdminIsSessionExpired,
  OcAdminApiError,
  ocAdminTenantsApi,
  ocAdminLeadsApi,
} from "@/lib/oc-admin-api";
import { useOcAdminStore, type OcInternalRole } from "@/store/oc-admin-store";

const OC_ADMIN_API_BASE = import.meta.env.VITE_OC_ADMIN_API_BASE ?? "/api/admin";

// Lazy-load pages for code splitting
const OcTenantsPage         = lazy(() => import("@/pages/OcTenantsPage"));
const OcTenantDetailPage    = lazy(() => import("@/pages/OcTenantDetailPage"));
const OcSystemHealthPage    = lazy(() => import("@/pages/OcSystemHealthPage"));
const OcLeadsPipelinePage   = lazy(() => import("@/pages/OcLeadsPipelinePage"));
const OcContractsPage       = lazy(() => import("@/pages/OcContractsPage"));
const OcBillingPage         = lazy(() => import("@/pages/OcBillingPage"));
const OcResourcesPricingPage   = lazy(() => import("@/pages/OcResourcesPricingPage"));
const OcResourcesHecvatPage    = lazy(() => import("@/pages/OcResourcesHecvatPage"));
const OcResourcesWorkflowsPage = lazy(() => import("@/pages/OcResourcesWorkflowsPage"));
const OcResourcesTechDocsPage  = lazy(() => import("@/pages/OcResourcesTechDocsPage"));
const OcResourcesUserManualPage = lazy(() => import("@/pages/OcResourcesUserManualPage"));
const OcResourcesAgentUiPage    = lazy(() => import("@/pages/OcResourcesAgentUiPage"));
const OcResourcesBusinessCardsPage = lazy(() => import("@/pages/OcResourcesBusinessCardsPage"));
const OcResourcesHybridLabPage  = lazy(() => import("@/pages/OcResourcesHybridLabPage"));
const OcResourcesStagingPage    = lazy(() => import("@/pages/OcResourcesStagingPage"));
const OcCampaignsPage       = lazy(() => import("@/pages/OcCampaignsPage"));
const OcInternalUsersPage   = lazy(() => import("@/pages/OcInternalUsersPage"));
const OcSupportPage         = lazy(() => import("@/pages/OcSupportPage"));
const OcAdminNotFoundPage   = lazy(() => import("@/pages/OcAdminNotFoundPage"));
const OcAdminLoginPage      = lazy(() => import("@/pages/OcAdminLoginPage"));

function OcPageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[40vh]">
      <div className="flex items-center gap-3 text-slate-500 text-xs font-mono">
        <span className="w-1.5 h-1.5 rounded-full bg-oc-teal-500 animate-pulse" />
        Loading…
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth guard — validates session before rendering protected content
// ---------------------------------------------------------------------------

type OcAuthState = "loading" | "authenticated" | "unauthenticated";

function OcAdminRequireAuth({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<OcAuthState>("loading");
  const location = useLocation();
  const { setInternalUser, clearSession } = useOcAdminStore();
  const qc = useQueryClient();

  useEffect(() => {
    const token = ocAdminGetSessionToken();
    if (!token) {
      setAuthState("unauthenticated");
      return;
    }
    fetch(`${OC_ADMIN_API_BASE}/auth/me`, {
      headers: { "X-OC-Admin-Session-Token": token },
    })
      .then((res) => {
        if (!res.ok) throw new OcAdminApiError(res.status, "UNAUTHORIZED", "Unauthorized");
        return res.json() as Promise<{ user: { email: string; name: string; role: string } }>;
      })
      .then((me) => {
        setInternalUser({
          id: me.user.email,
          name: me.user.name,
          email: me.user.email,
          role: me.user.role as OcInternalRole,
          avatarUrl: null,
        });
        // Prefetch the two most-visited pages in parallel while the JS chunks load
        void qc.prefetchQuery({ queryKey: ["admin", "tenants"], queryFn: ocAdminTenantsApi.list, staleTime: 2 * 60_000 });
        void qc.prefetchQuery({ queryKey: ["admin", "leads"],   queryFn: ocAdminLeadsApi.list,   staleTime: 2 * 60_000 });
        setAuthState("authenticated");
      })
      .catch((err) => {
        if (err instanceof OcAdminApiError && err.status === 401) {
          ocAdminClearSessionToken();
          clearSession();
        }
        setAuthState("unauthenticated");
      });
  }, []);

  // Inactivity + max-session watcher (active only when authenticated)
  useEffect(() => {
    if (authState !== "authenticated") return;

    const EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart"] as const;
    EVENTS.forEach((ev) => window.addEventListener(ev, ocAdminRecordActivity, { passive: true }));

    const interval = setInterval(() => {
      if (ocAdminIsSessionExpired()) {
        ocAdminClearSessionToken();
        clearSession();
        setAuthState("unauthenticated");
      }
    }, 60_000);

    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, ocAdminRecordActivity));
      clearInterval(interval);
    };
  }, [authState]);

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500 text-xs font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-oc-teal-500 animate-pulse" />
          Loading…
        </div>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Protected app shell
// ---------------------------------------------------------------------------

function OcAdminShell() {
  return (
    <OcAdminRequireAuth>
      <OcAdminLayout>
        <Suspense fallback={<OcPageLoader />}>
          <Routes>
            <Route index element={<Navigate to="tenants" replace />} />
            <Route path="tenants"              element={<OcTenantsPage />} />
            <Route path="tenants/:tenantId"    element={<OcTenantDetailPage />} />
            <Route path="system-health"        element={<OcSystemHealthPage />} />
            <Route path="leads"                element={<OcLeadsPipelinePage />} />
            <Route path="contracts"            element={<OcContractsPage />} />
            <Route path="billing"              element={<OcBillingPage />} />
            <Route path="campaigns"            element={<OcCampaignsPage />} />
            <Route path="support"             element={<OcSupportPage />} />
            <Route path="resources/pricing"    element={<OcResourcesPricingPage />} />
            <Route path="resources/hecvat"     element={<OcResourcesHecvatPage />} />
            <Route path="resources/workflows"  element={<OcResourcesWorkflowsPage />} />
            <Route path="resources/tech-docs"    element={<OcResourcesTechDocsPage />} />
            <Route path="resources/user-manual" element={<OcResourcesUserManualPage />} />
            <Route path="resources/agent-ui"    element={<OcResourcesAgentUiPage />} />
            <Route path="resources/business-cards" element={<OcResourcesBusinessCardsPage />} />
            <Route path="resources/hybrid-lab"     element={<OcResourcesHybridLabPage />} />
            <Route path="resources/staging"        element={<OcResourcesStagingPage />} />
            <Route path="users"                element={<OcInternalUsersPage />} />
            <Route path="*"                    element={<OcAdminNotFoundPage />} />
          </Routes>
        </Suspense>
      </OcAdminLayout>
    </OcAdminRequireAuth>
  );
}

// ---------------------------------------------------------------------------
// Root app
// ---------------------------------------------------------------------------

export default function OcAdminApp() {
  return (
    <Suspense fallback={<OcPageLoader />}>
      <Routes>
        <Route path="/login" element={<OcAdminLoginPage />} />
        <Route path="/*" element={<OcAdminShell />} />
      </Routes>
    </Suspense>
  );
}
